import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import * as AgentSelectors from '../../../store/agents.selectors';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  takeUntil,
  take,
} from 'rxjs';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import {
  ICustomer,
  ILocation,
} from 'src/app/features/customers/models/customers.models';
import { IAgent } from '../../../models/agents.models';
import * as agentsActions from '../../../store/agents.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AgentPortfolioAddDialogComponent } from './agent-portfolio-add-dialog/agent-portfolio-add-dialog.component';

@Component({
  selector: 'app-agent-portfolio',
  templateUrl: './agent-portfolio.component.html',
  styleUrls: ['./agent-portfolio.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({ height: '*', opacity: 1 })),
      state(
        'collapsed',
        style({ height: '0px', opacity: 0, overflow: 'hidden', padding: '0px' })
      ),
      transition('expanded <=> collapsed', animate('250ms ease-in-out')),
    ]),
  ],
})
export class AgentPortfolioComponent implements OnInit, OnChanges {
  @Output() selectedLocationsChanged = new EventEmitter<boolean>();
  @Input() triggerAddPortfolio: boolean = false;
  @Input() triggerDelete: boolean = false;

  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  agent$: Observable<IAgent | null> = this.store.select(
    AgentSelectors.selectAgent
  );
  agent: IAgent | null = null;
  customers: ICustomer[] = [];

  expandedCompanies: number[] = [];
  selectedLocations: number[] = [];

  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;

  DATE_FORMAT = DATE_FORMAT;

  locationsToDelete: number[] = [];

  destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private router: Router,
    public dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.agent$
      .pipe(
        filter((agent) => agent != null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((agent: IAgent | null) => {
        this.agent = agent;
        if (agent && agent.companies_ids) {
          this.customers = agent.companies_ids
            .filter((customer) => customer.locations!.length > 0)
            .map((customer) => ({
              ...customer,
              locations: [...customer.locations!].sort((a, b) => {
                if (a.is_main && !b.is_main) return -1;
                if (!a.is_main && b.is_main) return 1;
                return a.name.localeCompare(b.name);
              }),
            }));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['triggerDelete'] && changes['triggerDelete'].currentValue) {
      this.locationsToDelete = [...this.selectedLocations];
      this.deleteConfirmation();
    }

    if (changes['triggerAddPortfolio'] && changes['triggerAddPortfolio'].currentValue) {
      this.openAddPortfolioDialog();
    }
  }

  emitSelectedLocationsChange() {
    this.selectedLocationsChanged.emit(this.selectedLocations.length > 0);
  }

  // Check if a company is expanded
  isCompanyExpanded(company: any): boolean {
    return this.expandedCompanies.includes(company.id);
  }

  // Check if all locations in all companies are selected
  isAllLocationsSelected(): boolean {
    const allLocationIds = this.customers.flatMap((company: ICustomer) =>
      company.locations!.map((loc: ILocation) => loc.id)
    );
    return allLocationIds.length
      ? allLocationIds.every((id: any) => this.selectedLocations.includes(id))
      : false;
  }

  // Check if at least one location is selected but not all
  isPartialSelectedAll(): boolean {
    const allLocationIds = this.customers.flatMap((company: ICustomer) =>
      company.locations!.map((loc: ILocation) => loc.id)
    );
    const selectedCount = allLocationIds.filter((id: any) =>
      this.selectedLocations.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allLocationIds.length;
  }

  // Check if a company has all its locations selected
  isCompanyFullySelected(companyId: number): boolean {
    const company = this.customers.find(
      (c: { id: number }) => c.id === companyId
    );
    if (!company || company.locations!.length === 0) return false;
    return company.locations!.every((loc: ILocation) =>
      this.selectedLocations.includes(loc.id!)
    );
  }

  // Check if a company has some (but not all) of its locations selected
  isCompanyPartiallySelected(companyId: number): boolean {
    const company = this.customers.find(
      (c: { id: number }) => c.id === companyId
    );
    if (!company) return false;
    const locationIds = company.locations!.map((loc: ILocation) => loc.id);
    const selectedCount = locationIds.filter((id: any) =>
      this.selectedLocations.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < locationIds.length;
  }

  toggleCompanyExpand(company: any): void {
    this.expandedCompanies = this.expandedCompanies || [];
    const index = this.expandedCompanies.indexOf(company.id);
    if (index > -1) {
      this.expandedCompanies.splice(index, 1);
    } else {
      this.expandedCompanies.push(company.id);
    }
  }

  toggleAllLocations(checked: boolean) {
    const allIds = this.customers.flatMap((c: ICustomer) =>
      c.locations!.map((l: ILocation) => l.id!)
    );
    this.selectedLocations = checked ? [...allIds] : [];
    this.emitSelectedLocationsChange();
  }

  toggleCompanyLocations(companyId: number, checked: boolean) {
    const company = this.customers.find(
      (c: { id: number }) => c.id === companyId
    );
    if (!company || company.locations?.length === 0) return;
    const locationIds = company.locations!.map((loc: ILocation) => loc.id!);

    this.selectedLocations = checked
      ? [...new Set([...this.selectedLocations, ...locationIds])]
      : this.selectedLocations.filter((id) => !locationIds.includes(id));
    this.emitSelectedLocationsChange();
  }

  toggleLocation(locationId: number, checked: boolean) {
    if (checked) {
      this.selectedLocations.push(locationId);
    } else {
      this.selectedLocations = this.selectedLocations.filter(
        (id) => id !== locationId
      );
    }
    this.emitSelectedLocationsChange();
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (!this.customers || !Array.isArray(this.customers)) return;

    this.customers = [...this.customers].sort((a: any, b: any) => {
      let valA: any = a[key];
      let valB: any = b[key];

      if (key === 'locations') {
        valA = a.locations?.length || 0;
        valB = b.locations?.length || 0;
      }

      let comparison = 0;

      // Improved date/null sorting: always put null/undefined after valid dates (for asc), before for desc
      const isValADate = typeof valA === 'string' && !isNaN(Date.parse(valA));
      const isValBDate = typeof valB === 'string' && !isNaN(Date.parse(valB));

      if (isValADate || isValBDate) {
        // If both are null/undefined/empty, treat as equal
        if (!valA && !valB) {
          comparison = 0;
        } else if (!valA) {
          // valA is null/undefined/empty, so it should be after valB in asc, before in desc
          comparison = 1;
        } else if (!valB) {
          // valB is null/undefined/empty, so it should be after valA in asc, before in desc
          comparison = -1;
        } else {
          // Both are valid dates
          const timeA = new Date(valA).getTime();
          const timeB = new Date(valB).getTime();
          comparison = timeA - timeB;
        }
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else {
        // Default to string comparison
        comparison = String(valA).localeCompare(String(valB));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
  }

  trackByCustomerId(_index: number, customer: ICustomer): string | number {
    return customer.id!;
  }

  goToDetails(location: ILocation) {
    this.router.navigate(['/companies/', location.id]);
  }

  openAddPortfolioDialog() {
    this.dialog.open(AgentPortfolioAddDialogComponent, {
      data: { 
        agent: this.agent, 
        locations_ids: this.customers.flatMap((customer: ICustomer) => customer.locations!.map((location: ILocation) => location.id!)) 
      },
    });
  }

  deleteLocations(): void {
    if (!this.agent$ || !this.locationsToDelete.length) return;

    this.agent$.pipe(take(1)).subscribe((agent) => {
      if (!agent || !agent.id) return;

      // Get all location IDs except the ones to be deleted
      const updatedLocationIds = agent.companies_ids!
        .flatMap(company => company.locations || [])
        .map(location => location.id)
        .filter(id => !this.locationsToDelete.includes(id!));

      // Dispatch the update action with only the location_ids field
      this.store.dispatch(
        agentsActions.UpdateAgent({
          id: agent.id,
          agent: { location_ids: updatedLocationIds as number[] },
        })
      );
    });
  }

  deleteConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: 'assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove')
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteLocations();
      }

      this.locationsToDelete = [];
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
